import React, { useEffect, useRef, useState, useMemo } from "react";

const sizeClasses = {
  xs: "px-2 py-0",
  sm: "px-3 py-1.5",
  md: "px-3 py-2",
  lg: "px-4 py-2.5",
  xl: "px-5 py-3",
};

const fontSizeClasses = {
  xs: "text-[12px]",
  sm: "text-[12px]",
  md: "text-[14px]",
  lg: "text-[16px]",
  xl: "text-[18px]",
};

const CustomInput = ({
  value,
  onChange,
  placeholder = "Enter text...",
  size = "md",
  rounded = "rounded",
  type = "text",
  disabled = false,
  maxNumber,
  shadow = false,
  onKeyDown,
  onFocus,
  onPaste,
  suggestionsList = [],
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef(null);
  const getLabel = (s) =>
    typeof s === "string" ? s : s?.label ?? s?.value ?? "";
  const filteredSuggestions = useMemo(() => {
    const q = String(value ?? "")
      .toLowerCase()
      .trim();
    if (!q) return [];
    return (Array.isArray(suggestionsList) ? suggestionsList : []).filter((s) =>
      getLabel(s).toLowerCase().includes(q)
    );
  }, [suggestionsList, value]);
  useEffect(() => {
    if (filteredSuggestions.length === 0) {
      setShowSuggestions(false);
    } else {
      const q = String(value ?? "")
        .toLowerCase()
        .trim();
      const hasMatches =
        q.length > 0 &&
        (Array.isArray(suggestionsList) ? suggestionsList : []).some((s) =>
          getLabel(s).toLowerCase().includes(q)
        );
      setShowSuggestions(hasMatches);
    }
  }, [filteredSuggestions]);
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (
        showSuggestions &&
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [showSuggestions]);
  const handleChange = (e) => {
    let newValue = e.target.value;
    if (type === "number") {
      const numericValue = parseFloat(newValue);
      // ✅ Only check maxNumber if it is explicitly provided (not null/undefined)  / / mahee-12-07-25 update
      if (
        !isNaN(numericValue) &&
        maxNumber !== undefined &&
        maxNumber !== null &&
        numericValue > maxNumber
      ) {
        newValue = maxNumber;
      }
    }
    onChange(newValue);
    const q = String(newValue ?? "")
      .toLowerCase()
      .trim();
    const hasMatches =
      q.length > 0 &&
      (Array.isArray(suggestionsList) ? suggestionsList : []).some((s) =>
        getLabel(s).toLowerCase().includes(q)
      );
    setShowSuggestions(hasMatches);
  };

  const handlePaste = (e) => {
    if (onPaste) {
      onPaste(e);
    }
  };
  const handleSelectSuggestion = (s) => {
    const val = typeof s === "string" ? s : s?.value ?? s?.label ?? "";
    onChange(val);
    setShowSuggestions(false);
  };
  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type={type}
        value={value ?? ""}
        disabled={disabled}
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={onFocus}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        className={`w-full border focus:outline-none
    ${shadow ? "border-gray-50 inputShadow" : "border-gray-300"}
    ${sizeClasses[size]} ${fontSizeClasses[size]} ${rounded}
    ${
      disabled
        ? "border-neutrals-200 bg-neutrals-200 cursor-not-allowed text-black"
        : "bg-white hover:border-primary focus:border-primary"
    }
  `}
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute left-0 right-0 mt-1 border border-gray-300 bg-white rounded shadow-md z-10">
          <ul className="max-h-48 overflow-auto">
            {filteredSuggestions.map((s, idx) => (
              <li
                key={idx}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onMouseDown={() => handleSelectSuggestion(s)}
              >
                {getLabel(s)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomInput;
